import type { ModelNode, ModelEdge } from '../types/conceptMap';
import type { CanvasSize, PositionedNode } from './types';

const LEVEL_SPACING_Y = 140;
const MIN_NODE_SPACING_X = 160;

/**
 * Hierarchy (tree) layout: root at top center, children spread horizontally per depth level.
 * Pure function — same input always produces same output.
 */
export function hierarchyLayout(
  nodes: ModelNode[],
  edges: ModelEdge[],
  canvasSize: CanvasSize
): PositionedNode[] {
  if (nodes.length === 0) return [];

  // Build adjacency: parent → children
  const childrenMap = new Map<string, string[]>();
  for (const edge of edges) {
    const children = childrenMap.get(edge.source) ?? [];
    children.push(edge.target);
    childrenMap.set(edge.source, children);
  }

  // Find root (depth 0)
  const root = nodes.find((n) => n.depth === 0);
  if (!root) {
    // Fallback: just center everything
    return nodes.map((n, i) => ({ ...n, x: canvasSize.width / 2, y: i * LEVEL_SPACING_Y }));
  }

  // Group nodes by depth
  const byDepth = new Map<number, ModelNode[]>();
  for (const node of nodes) {
    const group = byDepth.get(node.depth) ?? [];
    group.push(node);
    byDepth.set(node.depth, group);
  }

  // Position each depth level
  const positions = new Map<string, { x: number; y: number }>();
  const centerX = canvasSize.width / 2;

  for (const [depth, levelNodes] of byDepth) {
    const y = 60 + depth * LEVEL_SPACING_Y;
    const totalWidth = (levelNodes.length - 1) * MIN_NODE_SPACING_X;
    const startX = centerX - totalWidth / 2;

    levelNodes.forEach((node, index) => {
      positions.set(node.id, {
        x: startX + index * MIN_NODE_SPACING_X,
        y,
      });
    });
  }

  return nodes.map((node) => {
    const pos = positions.get(node.id) ?? { x: centerX, y: 60 };
    return { ...node, x: pos.x, y: pos.y };
  });
}
